(function () {
  'use strict';

  var API_KEY = 'KjsSeasonalHero';
  var ROOT_SELECTOR = '[data-kjs-hero-slider]';
  var SLIDE_SELECTOR = '[data-kjs-hero-slide]';
  var FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]';

  function SeasonalHero(root) {
    this.root = root;
    this.slides = Array.prototype.slice.call(root.querySelectorAll(SLIDE_SELECTOR));
    this.slidesContainer = root.querySelector('.kjs-hero-slides');
    this.previousButton = root.querySelector('[data-kjs-hero-prev]');
    this.nextButton = root.querySelector('[data-kjs-hero-next]');
    this.toggleButton = root.querySelector('[data-kjs-hero-toggle]');
    this.dots = Array.prototype.slice.call(root.querySelectorAll('[data-kjs-hero-dot]'));
    this.status = root.querySelector('[data-kjs-hero-status]');
    this.interval = Math.max(5000, Number(root.getAttribute('data-interval-ms')) || 7000);
    this.autoplayEnabled = root.getAttribute('data-autoplay') === 'true';
    this.currentIndex = Math.max(0, this.slides.findIndex(function (slide) {
      return slide.classList.contains('is-active');
    }));
    this.manualPause = false;
    this.hoverPause = false;
    this.focusPause = false;
    this.pageVisible = !document.hidden;
    this.inViewport = true;
    this.timer = null;
    this.touchStart = null;
    this.cleanups = [];
    this.resizeObserver = null;
    this.intersectionObserver = null;
    this.motionQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
    this.reducedMotion = Boolean(this.motionQuery && this.motionQuery.matches);
    if (this.reducedMotion) this.manualPause = true;
  }

  SeasonalHero.prototype.listen = function (target, eventName, handler, options) {
    if (!target) return;
    target.addEventListener(eventName, handler, options);
    this.cleanups.push(function () {
      target.removeEventListener(eventName, handler, options);
    });
  };

  SeasonalHero.prototype.init = function () {
    var self = this;
    if (!this.slides.length) return;
    this.root.setAttribute('data-kjs-hero-ready', 'true');

    this.slides.forEach(function (slide, index) {
      slide.hidden = false;
      slide.setAttribute('aria-label', 'Promotion ' + (index + 1) + ' of ' + self.slides.length);
    });

    this.dots.forEach(function (dot, index) {
      var heading = self.slides[index] && self.slides[index].querySelector('.kjs-v5-hero__title');
      var label = heading && heading.textContent.trim();
      dot.setAttribute('aria-label', label ? 'Show promotion: ' + label : 'Show promotion ' + (index + 1));
      self.listen(dot, 'click', function () {
        self.pauseAfterInteraction();
        self.show(index, true);
      });
    });

    this.listen(this.previousButton, 'click', function () {
      self.pauseAfterInteraction();
      self.show(self.currentIndex - 1, true);
    });
    this.listen(this.nextButton, 'click', function () {
      self.pauseAfterInteraction();
      self.show(self.currentIndex + 1, true);
    });
    this.listen(this.toggleButton, 'click', function () {
      self.manualPause = !self.manualPause;
      self.updateToggle();
      self.schedule();
    });

    this.listen(this.root, 'mouseenter', function () {
      self.hoverPause = true;
      self.clearTimer();
    });
    this.listen(this.root, 'mouseleave', function () {
      self.hoverPause = false;
      self.schedule();
    });
    this.listen(this.root, 'focusin', function () {
      self.focusPause = true;
      self.clearTimer();
    });
    this.listen(this.root, 'focusout', function (event) {
      if (event.relatedTarget && self.root.contains(event.relatedTarget)) return;
      self.focusPause = false;
      self.schedule();
    });

    this.listen(this.root, 'touchstart', function (event) {
      if (!event.touches || event.touches.length !== 1) return;
      if (event.target.closest('a, button, input, select, textarea, [role="button"]')) return;
      self.touchStart = {
        x: event.touches[0].clientX,
        y: event.touches[0].clientY
      };
    }, { passive: true });
    this.listen(this.root, 'touchend', function (event) {
      if (!self.touchStart || !event.changedTouches || event.changedTouches.length !== 1) {
        self.touchStart = null;
        return;
      }
      var xDistance = event.changedTouches[0].clientX - self.touchStart.x;
      var yDistance = event.changedTouches[0].clientY - self.touchStart.y;
      self.touchStart = null;
      if (Math.abs(xDistance) < 40 || Math.abs(xDistance) <= Math.abs(yDistance)) return;
      self.pauseAfterInteraction();
      self.show(xDistance < 0 ? self.currentIndex + 1 : self.currentIndex - 1, true);
    }, { passive: true });

    this.listen(document, 'visibilitychange', function () {
      self.pageVisible = !document.hidden;
      self.schedule();
    });
    this.listen(window, 'resize', function () {
      self.syncMobileHeight();
    });

    if (this.motionQuery) {
      var motionHandler = function (event) {
        self.reducedMotion = event.matches;
        if (event.matches) self.manualPause = true;
        self.updateToggle();
        self.schedule();
      };
      if (this.motionQuery.addEventListener) {
        this.motionQuery.addEventListener('change', motionHandler);
        this.cleanups.push(function () { self.motionQuery.removeEventListener('change', motionHandler); });
      } else if (this.motionQuery.addListener) {
        this.motionQuery.addListener(motionHandler);
        this.cleanups.push(function () { self.motionQuery.removeListener(motionHandler); });
      }
    }

    if ('IntersectionObserver' in window) {
      this.intersectionObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.target !== self.root) return;
          self.inViewport = entry.isIntersecting && entry.intersectionRatio >= 0.1;
          self.schedule();
        });
      }, { threshold: [0, 0.1] });
      this.intersectionObserver.observe(this.root);
    }

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(function () {
        self.syncMobileHeight();
      });
      this.slides.forEach(function (slide) { self.resizeObserver.observe(slide); });
    }

    this.slides.forEach(function (slide) {
      Array.prototype.forEach.call(slide.querySelectorAll('img'), function (image) {
        if (!image.complete) self.listen(image, 'load', function () { self.syncMobileHeight(); });
      });
    });

    if (!this.autoplayEnabled && this.toggleButton) this.toggleButton.hidden = true;
    this.show(this.currentIndex, false);
    this.updateToggle();
    window.requestAnimationFrame(function () { self.syncMobileHeight(); });
    this.schedule();
  };

  SeasonalHero.prototype.setInteractive = function (slide, active) {
    if ('inert' in slide) slide.inert = !active;
    if (!active) slide.setAttribute('inert', '');
    else slide.removeAttribute('inert');

    Array.prototype.forEach.call(slide.querySelectorAll(FOCUSABLE_SELECTOR), function (element) {
      if (!active) {
        if (!element.hasAttribute('data-kjs-hero-tabindex')) {
          element.setAttribute('data-kjs-hero-tabindex', element.hasAttribute('tabindex') ? element.getAttribute('tabindex') : '__none__');
        }
        element.setAttribute('tabindex', '-1');
      } else if (element.hasAttribute('data-kjs-hero-tabindex')) {
        var previous = element.getAttribute('data-kjs-hero-tabindex');
        if (previous === '__none__') element.removeAttribute('tabindex');
        else element.setAttribute('tabindex', previous);
        element.removeAttribute('data-kjs-hero-tabindex');
      }
    });
  };

  SeasonalHero.prototype.show = function (requestedIndex, announce) {
    var self = this;
    if (!this.slides.length) return;
    var count = this.slides.length;
    this.currentIndex = ((requestedIndex % count) + count) % count;

    this.slides.forEach(function (slide, index) {
      var active = index === self.currentIndex;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      self.setInteractive(slide, active);
    });
    this.dots.forEach(function (dot, index) {
      var active = index === self.currentIndex;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-current', active ? 'true' : 'false');
    });

    if (announce && this.status) this.status.textContent = 'Promotion ' + (this.currentIndex + 1) + ' of ' + count;
    else if (this.status) this.status.textContent = '';
    this.syncMobileHeight();
  };

  SeasonalHero.prototype.pauseAfterInteraction = function () {
    if (!this.autoplayEnabled) return;
    this.manualPause = true;
    this.clearTimer();
    this.updateToggle();
  };

  SeasonalHero.prototype.canAutoplay = function () {
    return this.autoplayEnabled &&
      this.slides.length > 1 &&
      !this.reducedMotion &&
      !this.manualPause &&
      !this.hoverPause &&
      (!this.focusPause || document.activeElement === this.toggleButton) &&
      this.pageVisible &&
      this.inViewport;
  };

  SeasonalHero.prototype.schedule = function () {
    var self = this;
    this.clearTimer();
    if (!this.canAutoplay()) return;
    this.timer = window.setTimeout(function () {
      self.show(self.currentIndex + 1, false);
      self.schedule();
    }, this.interval);
  };

  SeasonalHero.prototype.clearTimer = function () {
    if (!this.timer) return;
    window.clearTimeout(this.timer);
    this.timer = null;
  };

  SeasonalHero.prototype.updateToggle = function () {
    if (!this.toggleButton) return;
    this.toggleButton.hidden = !this.autoplayEnabled || this.reducedMotion;
    var paused = this.manualPause;
    this.toggleButton.setAttribute('aria-label', paused ? 'Play slideshow' : 'Pause slideshow');
    this.toggleButton.removeAttribute('aria-pressed');
    var icon = this.toggleButton.querySelector('i');
    if (icon) {
      icon.classList.toggle('fa-play', paused);
      icon.classList.toggle('fa-pause', !paused);
    }
  };

  SeasonalHero.prototype.syncMobileHeight = function () {
    if (!this.slidesContainer) return;
    if (!window.matchMedia || !window.matchMedia('(max-width: 720px)').matches) {
      this.slidesContainer.style.minHeight = '';
      return;
    }
    var tallest = this.slides.reduce(function (height, slide) {
      return Math.max(height, slide.scrollHeight);
    }, 0);
    if (tallest > 0 && Math.abs((parseFloat(this.slidesContainer.style.minHeight) || 0) - tallest) > 1) {
      this.slidesContainer.style.minHeight = Math.ceil(tallest) + 'px';
    }
  };

  SeasonalHero.prototype.destroy = function () {
    this.clearTimer();
    this.cleanups.forEach(function (cleanup) { cleanup(); });
    this.cleanups = [];
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.intersectionObserver) this.intersectionObserver.disconnect();
    this.root.removeAttribute('data-kjs-hero-ready');
    delete this.root.__kjsSeasonalHero;
  };

  function initAll(scope) {
    var rootScope = scope || document;
    var roots = [];
    if (rootScope.matches && rootScope.matches(ROOT_SELECTOR)) roots.push(rootScope);
    roots = roots.concat(Array.prototype.slice.call(rootScope.querySelectorAll ? rootScope.querySelectorAll(ROOT_SELECTOR) : []));
    roots.forEach(function (root) {
      if (root.__kjsSeasonalHero) return;
      var carousel = new SeasonalHero(root);
      root.__kjsSeasonalHero = carousel;
      carousel.init();
    });
  }

  function destroyAll(scope) {
    if (!scope) return;
    var roots = [];
    if (scope.matches && scope.matches(ROOT_SELECTOR)) roots.push(scope);
    roots = roots.concat(Array.prototype.slice.call(scope.querySelectorAll ? scope.querySelectorAll(ROOT_SELECTOR) : []));
    roots.forEach(function (root) {
      if (root.__kjsSeasonalHero) root.__kjsSeasonalHero.destroy();
    });
  }

  if (window[API_KEY]) {
    window[API_KEY].initAll(document);
    return;
  }

  window[API_KEY] = { initAll: initAll, destroyAll: destroyAll };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  else initAll(document);

  document.addEventListener('shopify:section:load', function (event) { initAll(event.target); });
  document.addEventListener('shopify:section:unload', function (event) { destroyAll(event.target); });
  document.addEventListener('shopify:block:select', function (event) {
    var slide = event.target && event.target.closest ? event.target.closest(SLIDE_SELECTOR) : null;
    var root = slide && slide.closest(ROOT_SELECTOR);
    if (!root || !root.__kjsSeasonalHero) return;
    var index = root.__kjsSeasonalHero.slides.indexOf(slide);
    if (index >= 0) root.__kjsSeasonalHero.show(index, false);
  });
})();

